import useSEO from '@/hooks/useSEO';
import { strategyCostFAQs } from './data';
import StrategyOverviewSection from './components/StrategyOverviewSection';
import CostBreakdownSection from './components/CostBreakdownSection';
import WhichCostDominatesSection from './components/WhichCostDominatesSection';
import CostOptimizationPlaybook from './components/CostOptimizationPlaybook';
import AccountTypeMatchmaker from './components/AccountTypeMatchmaker';
import PerTradeCostReference from './components/PerTradeCostReference';
import StrategyCostFAQSection from './components/StrategyCostFAQSection';
import RelatedGuidesSection from './components/RelatedGuidesSection';

export default function ForexTradingCostsByStrategy() {
  useSEO({
    /* @replace:strategy-title */

    title: 'Forex Trading Costs by Strategy: Scalping to Position',
    /* @replace:strategy-description */

    description: 'Compare how spread, commission, swap and slippage affect scalping, day trading, swing trading and position trading, with practical cost controls.',
    /* @replace:strategy-keywords */

    keywords: 'forex trading costs by strategy, scalping costs, day trading costs, swing trading costs, position trading costs, commission vs swap',
    /* @replace:strategy-canonical */

    canonical: '/forex-trading-costs-by-strategy/',
  });

  const faqStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: strategyCostFAQs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <div className="min-h-screen">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqStructuredData) }}
      />

      {/* Hero */}
      <section className="relative w-full overflow-hidden bg-background-950">
        {/* Background glow layer */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-[-15%] left-1/2 -translate-x-1/2 w-[1000px] h-[1000px] rounded-full bg-primary-900/20 blur-[160px]" />
          <div className="absolute bottom-[-10%] right-[5%] w-[600px] h-[600px] rounded-full bg-accent-900/12 blur-[130px]" />
          <div className="absolute top-[35%] left-[-8%] w-[500px] h-[500px] rounded-full bg-secondary-900/8 blur-[110px]" />
        </div>

        <div className="relative max-w-main mx-auto px-4 md:px-6 pt-16 md:pt-28 pb-20 md:pb-28 text-center">
          {/* H1 */}
          <h1 className="text-[40px] md:text-[64px] lg:text-[72px] font-bold text-foreground-50 leading-[1.06] tracking-tight mb-5">
            Forex Trading Costs
            <br className="hidden md:block" />
            {' '}by{' '}
            <span className="text-accent-400">Strategy Type</span>
          </h1>

          {/* Elegant divider */}
          <div className="flex items-center justify-center gap-4 mb-7">
            <div className="w-12 md:w-24 h-[1px] bg-gradient-to-r from-transparent to-foreground-800/40" />
            <div className="w-1.5 h-1.5 rounded-full bg-accent-500/50" />
            <div className="w-12 md:w-24 h-[1px] bg-gradient-to-l from-transparent to-foreground-800/40" />
          </div>

          {/* Description */}
          <p className="text-[16px] md:text-[18px] text-foreground-400 leading-relaxed max-w-[560px] mx-auto mb-14">
            Your trading style determines which cost matters most. A scalper&apos;s biggest expense is commission. A position trader&apos;s is swap.{' '}
            <span className="text-foreground-300 font-medium">Know your enemy.</span>
          </p>

          {/* Strategy Comparison Strip — no cards, no icons, pure typography */}
          <div className="max-w-[960px] mx-auto">
            {/* Desktop: 4 cols with vertical dividers. Mobile: 2 cols */}
            <div className="grid grid-cols-2 md:grid-cols-4">
              {/* Scalping */}
              <div className="relative flex flex-col items-center text-center px-4 py-6 md:px-5 md:py-8 border-r border-b border-foreground-800/20 md:border-b-0">
                <div className="w-10 h-[3px] rounded-full bg-accent-500 mb-5" />
                <div className="text-[22px] md:text-[26px] font-bold tracking-tight text-accent-400 mb-2">
                  Scalping
                </div>
                <div className="text-sm font-medium text-foreground-300 mb-1.5">
                  Seconds–minutes
                </div>
                <div className="text-xs text-foreground-500">
                  Commission dominant
                </div>
              </div>

              {/* Day Trading */}
              <div className="relative flex flex-col items-center text-center px-4 py-6 md:px-5 md:py-8 border-b border-foreground-800/20 md:border-b-0 md:border-r">
                <div className="w-10 h-[3px] rounded-full bg-primary-400 mb-5" />
                <div className="text-[22px] md:text-[26px] font-bold tracking-tight text-primary-400 mb-2">
                  Day Trading
                </div>
                <div className="text-sm font-medium text-foreground-300 mb-1.5">
                  Minutes–hours
                </div>
                <div className="text-xs text-foreground-500">
                  Spread + Commission
                </div>
              </div>

              {/* Swing Trading */}
              <div className="relative flex flex-col items-center text-center px-4 py-6 md:px-5 md:py-8 border-r border-foreground-800/20">
                <div className="w-10 h-[3px] rounded-full bg-secondary-400 mb-5" />
                <div className="text-[22px] md:text-[26px] font-bold tracking-tight text-secondary-400 mb-2">
                  Swing Trading
                </div>
                <div className="text-sm font-medium text-foreground-300 mb-1.5">
                  Days–weeks
                </div>
                <div className="text-xs text-foreground-500">
                  Swap takes over
                </div>
              </div>

              {/* Position */}
              <div className="relative flex flex-col items-center text-center px-4 py-6 md:px-5 md:py-8">
                <div className="w-10 h-[3px] rounded-full bg-primary-500 mb-5" />
                <div className="text-[22px] md:text-[26px] font-bold tracking-tight text-primary-500 mb-2">
                  Position
                </div>
                <div className="text-sm font-medium text-foreground-300 mb-1.5">
                  Weeks–months
                </div>
                <div className="text-xs text-foreground-500">
                  Swap = everything
                </div>
              </div>
            </div>

            {/* Bottom caption bar */}
            <div className="mt-6 md:mt-8 pt-5 border-t border-foreground-800/20 text-center">
              <p className="text-sm text-foreground-500">
                <span className="text-foreground-300 font-medium">Time scale increases →</span> cost dominance shifts from{' '}
                <span className="text-accent-400">entry</span> to{' '}
                <span className="text-secondary-400">holding</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      <StrategyOverviewSection />
      <CostBreakdownSection />
      <WhichCostDominatesSection />
      <CostOptimizationPlaybook />
      <AccountTypeMatchmaker />
      <PerTradeCostReference />
      <StrategyCostFAQSection />
      <RelatedGuidesSection />
    </div>
  );
}